import { useState } from 'react';
import { Layers, Bookmark, Wrench, ListTree, Pin, PinOff } from 'lucide-react';
import { useUIStore } from '@/state/uiStore';
import { cn } from '@/lib/cn';
import { LayersPanel } from '@/panels/LayersPanel';
import { LegendPanel } from '@/panels/LegendPanel';
import { BookmarksPanel } from '@/panels/BookmarksPanel';
import { ToolsPanel } from '@/panels/ToolsPanel';

const ITEMS = [
  { id: 'layers' as const, label: 'Layers', Icon: Layers },
  { id: 'legend' as const, label: 'Legend', Icon: ListTree },
  { id: 'bookmarks' as const, label: 'Bookmarks', Icon: Bookmark },
  { id: 'tools' as const, label: 'Tools', Icon: Wrench },
];

export function Sidebar() {
  const pinned = useUIStore((s) => s.sidebarOpen);
  const togglePinned = useUIStore((s) => s.toggleSidebar);
  const active = useUIStore((s) => s.activePanel);
  const setActive = useUIStore((s) => s.setActivePanel);

  const [hovered, setHovered] = useState(false);
  const showPanel = !!active && (pinned || hovered);
  const activeItem = ITEMS.find((i) => i.id === active);

  return (
    <aside
      className="relative z-20 flex h-full shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Rail — always visible */}
      <div className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border bg-background py-2">
        {ITEMS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActive(active === id ? null : id)}
            title={label}
            aria-label={label}
            className={cn(
              'grid size-10 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground',
              active === id && 'bg-accent text-accent-foreground',
            )}
          >
            <Icon className="size-4" />
          </button>
        ))}
      </div>

      {/* Panel — pinned (push) or hover (overlay) */}
      {active && (
        <div
          aria-hidden={!showPanel}
          className={cn(
            'flex h-full w-64 flex-col border-r border-border bg-background transition-[opacity,transform] duration-150',
            pinned
              ? 'static'
              : 'absolute left-14 top-0 z-30 shadow-xl',
            !showPanel && !pinned && 'pointer-events-none -translate-x-1 opacity-0',
          )}
        >
          <div className="flex h-10 shrink-0 items-center justify-between border-b border-border px-3">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {activeItem?.label ?? active}
            </div>
            <button
              type="button"
              onClick={togglePinned}
              title={pinned ? 'Unpin (auto-collapse)' : 'Pin open'}
              aria-label={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
              className="grid size-7 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              {pinned ? <PinOff className="size-3.5" /> : <Pin className="size-3.5" />}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <PanelContent />
          </div>
        </div>
      )}
    </aside>
  );
}

function PanelContent() {
  const active = useUIStore((s) => s.activePanel);
  if (!active) return null;
  if (active === 'layers') return <LayersPanel />;
  if (active === 'legend') return <LegendPanel />;
  if (active === 'bookmarks') return <BookmarksPanel />;
  if (active === 'tools') return <ToolsPanel />;
  return (
    <div className="p-3 text-xs text-muted-foreground">
      Content for {active} will appear here.
    </div>
  );
}
